import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackDemandTrapSend, trackDemandTrapShow } from './tracking';
import { DemandTrap, Error, NeedCode, Thank } from '../../components/ActionButton';
import { userSelector, currentOpenChatSelector } from '../../selectors';
import { sendDemand } from '../../services/sendDemand';
import { TActionButtonV2 } from '../../types/actionButton';
import { EDeviceType } from '../../types/deviceType';
import { TBubbleActionButton } from '../../types/messageBubble';
import { TActionButtonV3 } from '../../types/actionButtonV3';

type TDemandActionProps = {
  actionButton: TActionButtonV2 | TBubbleActionButton | TActionButtonV3;
};

export enum EDemandTrapScreen {
  Default = 'default',
  NeedCode = 'needCode',
  Sent = 'sent',
  SmsCodeError = 'smsCodeError',
  GlobalError = 'globalError',
}

export const DemandActionContainer: React.VFC<TDemandActionProps> = ({ actionButton }) => {
  const [screen, setScreen] = React.useState<EDemandTrapScreen>(EDemandTrapScreen.Default);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const user = useSelector(userSelector);
  const chat = useSelector(currentOpenChatSelector);
  const device = useDeviceType();

  const { link, text } = actionButton;
  const phone = ((user.isAuthenticated && user.phones && user.phones[0]) || {}).phone;
  const offerId = chat?.offer?.id;
  const isMobile = device !== EDeviceType.Desktop;

  React.useEffect(() => {
    trackDemandTrapShow();
  }, [link, phone]);

  React.useEffect(() => {
    if (screen === EDemandTrapScreen.Sent) {
      trackDemandTrapSend();
    }
  }, [screen]);

  const handleCloseModal = React.useCallback(() => {
    setScreen(EDemandTrapScreen.Default);
  }, []);

  const demandTrapSend = React.useCallback(
    async (confirmationCode?: string) => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);
      await sendDemand({ offerId, confirmationCode, setScreen });
      setIsLoading(false);
    },
    [isLoading, offerId],
  );

  if (!phone || !offerId) {
    return null;
  }

  return (
    <>
      <DemandTrap text={text} onClick={demandTrapSend} />
      {[EDemandTrapScreen.NeedCode, EDemandTrapScreen.SmsCodeError].includes(screen) && (
        <NeedCode
          onSubmit={demandTrapSend}
          onClose={handleCloseModal}
          onRetry={demandTrapSend}
          phone={phone as string}
          isLoading={isLoading}
          screen={screen}
          isMobile={isMobile}
        />
      )}
      {screen === EDemandTrapScreen.Sent && <Thank onClose={handleCloseModal} isMobile={isMobile} />}
      {screen === EDemandTrapScreen.GlobalError && <Error onClose={handleCloseModal} isMobile={isMobile} />}
    </>
  );
};
